import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

/** Estable entre renders: un arreglo nuevo por render fuerza a repintar. */
const EMPTY: string[] = [];

export function useHierarchyDrill<T extends {id: string}>(
  items: T[],
  resetKey: unknown,
  initialStack?: string[] | null,
) {
  // `initialStack` es el recorrido de la selección actual. `null` significa que no hay
  // selección, que no es lo mismo que una selección en la raíz: la lista
  // vacía sí pide volver arriba y la ausencia no pide nada.
  const initialStackRef = useRef<string[]>(initialStack ?? EMPTY);
  initialStackRef.current = initialStack ?? EMPTY;

  const [parentStack, setParentStack] = useState<string[]>(
    initialStack ?? EMPTY,
  );

  // Cambiar de contexto (proyecto, país, vista) invalida el nivel donde se
  // estaba: los padres de antes pueden no existir en la lista nueva.
  useEffect(() => {
    setParentStack(initialStackRef.current);
  }, [resetKey]);

  // Al cambiar la selección el panel la sigue hasta su nivel. Al limpiarla se
  // queda donde está: quitar el filtro pide ver a todos otra vez, no volver
  // a la raíz. Mientras se navega sin seleccionar, `initialStack` no cambia
  // y no estorba.
  const initialKey = initialStack ? initialStack.join('|') : null;
  useEffect(() => {
    if (initialKey === null) return;

    setParentStack(initialStackRef.current);
  }, [initialKey]);

  const itemsById = useMemo(
    () => new Map(items.map(item => [item.id, item])),
    [items],
  );

  /**
   * El nivel donde se está solo vale mientras sus padres sigan en la lista.
   * Cambiar de proyecto, de país o de filtro puede dejar fuera al manager en
   * el que se estaba, y entonces el panel se queda en un nivel que ya no
   * existe: sin resultados y sin migas para subir. Se corta en el primer
   * padre ausente, porque el resto del recorrido colgaba de él.
   *
   * Con la lista vacía no se poda: que no haya nada cargado todavía no prueba
   * que el padre haya desaparecido.
   */
  const validStack = useMemo(() => {
    if (parentStack.length === 0 || items.length === 0) return parentStack;

    const missing = parentStack.findIndex(id => !itemsById.has(id));

    return missing === -1 ? parentStack : parentStack.slice(0, missing);
  }, [parentStack, items.length, itemsById]);

  // Se lleva el estado a lo que de verdad se puede mostrar, para que bajar de
  // nivel no siga apilando sobre padres que ya no existen. Se compara el largo
  // y no la referencia: podar solo acorta, y si algún día dejara de hacerlo,
  // comparar referencias volvería a entrar aquí en cada render sin converger.
  useEffect(() => {
    if (validStack.length !== parentStack.length) setParentStack(validStack);
  }, [validStack, parentStack.length]);

  const drillDown = useCallback(
    (id: string) => setParentStack(prev => [...prev, id]),
    [],
  );

  const drillUp = useCallback(
    (id: string) =>
      setParentStack(prev => {
        const idx = prev.findIndex(p => p === id);
        return idx >= 0 ? prev.slice(0, idx) : prev;
      }),
    [],
  );

  const trailItems = useMemo(
    () =>
      validStack.map(id => itemsById.get(id)).filter((i): i is T => Boolean(i)),
    [validStack, itemsById],
  );

  return {
    trailItems,
    currentParentId: validStack[validStack.length - 1] ?? null,
    drillDown,
    drillUp,
  };
}
